export type TextDirection = "rtl" | "ltr";

/** Wire / public API default popup (no campaign). */
export interface ApiCampaignPopupDefault {
  title: string;
  body: string;
  ctaText: string;
  dismissText?: string;
  dir: TextDirection;
  showOncePerSession?: boolean;
  enabled?: boolean;
}

/** Wire / public API rule shape */
export interface ApiCampaignPopupRule {
  campaign: string | string[];
  title: string;
  body: string;
  ctaText: string;
  dismissText: string;
  dir: TextDirection;
  utm_source?: string;
  utm_medium?: string;
  enabled?: boolean;
  showOncePerSession?: boolean;
  updatedAt?: string;
}

/** Admin form / list row */
export interface CampaignPopupRule {
  enabled: boolean;
  /** Comma-separated in UI; serialized to string | string[] */
  campaign: string;
  utm_source?: string;
  utm_medium?: string;
  title: string;
  body: string;
  ctaText: string;
  dismissText: string;
  dir: TextDirection;
  showOncePerSession: boolean;
  updatedAt?: string;
}

export interface CampaignPopupRuleRow extends CampaignPopupRule {
  _clientId: string;
}

/** Popup shown when no utm_campaign rule matches. */
export interface CampaignPopupDefault {
  enabled: boolean;
  title: string;
  body: string;
  ctaText: string;
  dismissText: string;
  dir: TextDirection;
  showOncePerSession: boolean;
}

export interface CampaignPopupsConfig {
  /** Master switch: no popups on landing when false. */
  enabled: boolean;
  rules: CampaignPopupRule[];
  defaultPopup: CampaignPopupDefault;
}

export const CONFIG_KEY = "NEWBETTERME_CAMPAIGN_POPUPS";

export const BUILTIN_DEFAULT_POPUP: CampaignPopupDefault = {
  enabled: true,
  title: "تخفیف ویژه لیمومی برای ساکنین سایر استان‌ها فعال شد!",
  body:
    "به‌دلیل استقبال فراوان شما عزیزان،\n\n۲۰۰ هزار تومان تخفیف بیشتر +\nآنالیز رایگان (برای شناخت تایپ بدن)\nبرای شما فعال شده که می‌تونین همین الان دریافتش کنین...",
  ctaText: "فعال‌سازی تخفیفِ من!",
  dismissText: "",
  dir: "rtl",
  showOncePerSession: true,
};

export function createDefaultPopup(): CampaignPopupDefault {
  return { ...BUILTIN_DEFAULT_POPUP };
}

export function createEmptyRule(): CampaignPopupRule {
  return {
    enabled: true,
    campaign: "",
    utm_source: "",
    utm_medium: "",
    title: "",
    body: "",
    ctaText: "",
    dismissText: "",
    dir: "rtl",
    showOncePerSession: true,
  };
}

export function formatCampaignDisplay(campaign: string | string[]): string {
  if (Array.isArray(campaign)) return campaign.join(", ");
  return campaign || "";
}

export function parseCampaignInput(input: string): string | string[] {
  const parts = input
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return parts;
}

export function campaignValues(rule: CampaignPopupRule): string[] {
  const parsed = parseCampaignInput(rule.campaign);
  if (Array.isArray(parsed)) return parsed.map((c) => c.toLowerCase());
  if (parsed) return [parsed.toLowerCase()];
  return [];
}

export function ruleMatchKey(rule: CampaignPopupRule): string {
  const norm = (s?: string) => (s || "").trim().toLowerCase();
  const camps = campaignValues(rule).sort().join("|");
  return `${camps}|${norm(rule.utm_source)}|${norm(rule.utm_medium)}`;
}

export function findDuplicateIndex(
  rules: CampaignPopupRule[],
  candidate: CampaignPopupRule,
  excludeIndex?: number
): number {
  const key = ruleMatchKey(candidate);
  if (!campaignValues(candidate).length) return -1;
  return rules.findIndex(
    (r, i) => i !== excludeIndex && ruleMatchKey(r) === key
  );
}
